import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Button } from '../../layout/buttons';
import styles from '../CanvassInfo.module.css';
import { SimpleTable } from '../../layout/table/SimpleTable';
import reportStyles from './ReportSection.module.css';
import cbStyles from './ComprehensiveBusinessReport.module.css';
import { getCanvassReport, downloadReportJson, exportComprehensiveReportHtml, generateExecutiveSummary as generateExecutiveSummaryApi } from '../../../utils/reports';
import { fetchInterviewsByCanvassId } from '../../../utils/interviews';
import { Icon } from '../../layout/icons/Icons';
import analyticsService from '../../../utils/analytics';
import ComprehensiveBusinessReport from './ComprehensiveBusinessReport';
import InterviewModal from './InterviewModal';
import { logger } from '../../../utils/logger';

// Lightweight Radar (Spider) Chart using SVG, values expected in [0,1]
const RadarChart = ({ data = [], size = 280, maxValue = 1, onHoverChange }) => {
  const padding = 32;
  const radius = (size - padding * 2) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const count = data.length || 1;

  const angleForIndex = (i) => (Math.PI * 2 * i) / count - Math.PI / 2; // start at top
  const pointFor = (value, i) => {
    const v = Math.max(0, Math.min(value, maxValue)) / maxValue;
    const angle = angleForIndex(i);
    const r = v * radius;
    return [cx + Math.cos(angle) * r, cy + Math.sin(angle) * r];
  };

  const gridLevels = 4;
  const gridRadii = Array.from({ length: gridLevels }, (_, i) => ((i + 1) / gridLevels) * radius);
  const polygonPoints = data.map((d, i) => pointFor(Number(d.value ?? 0), i)).map(([x, y]) => `${x},${y}`).join(' ');

  return (
    <svg width={size} height={size} className={reportStyles.svg}>
      {/* concentric circles */}
      {gridRadii.map((r, idx) => (
        <circle key={idx} cx={cx} cy={cy} r={r} fill="none" stroke="#e5e7eb" />
      ))}
      {/* axes */}
      {data.map((_, i) => {
        const [x, y] = pointFor(maxValue, i);
        return <line key={`axis-${i}`} x1={cx} y1={cy} x2={x} y2={y} stroke="#e5e7eb" />
      })}
      {/* polygon */}
      {data.length > 2 && (
        <polygon points={polygonPoints} fill="rgba(59,130,246,0.2)" stroke="#3b82f6" strokeWidth={2} />
      )}
      {/* labels */}
      {data.map((d, i) => {
        const [x, y] = pointFor(maxValue + 0.08, i);
        return (
          <text
            key={`label-${i}`}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            className={reportStyles.svgLabel}
            onMouseEnter={() => onHoverChange && onHoverChange({ label: d.label, value: Number(d.value ?? 0) })}
            onMouseLeave={() => onHoverChange && onHoverChange(null)}
          >
            {d.label}
          </text>
        );
      })}
    </svg>
  );
};

// Helper function to parse markdown formatting (bold and italics)
const parseMarkdownFormatting = (text) => {
  // Handle bold text (**text**)
  let processedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Handle italic text (*text*)
  processedText = processedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  return processedText;
};

const ReportSection = ({ canvass }) => {
  const canvassId = useMemo(() => canvass?.canvassId || canvass?.id || '', [canvass]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportDoc, setReportDoc] = useState(null);
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [hoveredEmotion, setHoveredEmotion] = useState(null);
  const [sentimentCollapsed, setSentimentCollapsed] = useState(true);
  const [snippetsCollapsed, setSnippetsCollapsed] = useState(true);
  const [executiveSummaryCollapsed, setExecutiveSummaryCollapsed] = useState(true);
  const [executiveSummaryLoading, setExecutiveSummaryLoading] = useState(false);
  const [behavioursCollapsed, setBehavioursCollapsed] = useState(true);
  const [emotionalNarrativesCollapsed, setEmotionalNarrativesCollapsed] = useState(true);
  const [thematicAreasCollapsed, setThematicAreasCollapsed] = useState(true);
  const [businessIntelligenceCollapsed, setBusinessIntelligenceCollapsed] = useState(true);
  const [coreEmotionalDriversCollapsed, setCoreEmotionalDriversCollapsed] = useState(true);
  const [operationalRisksCollapsed, setOperationalRisksCollapsed] = useState(true);
  const [opportunitiesCollapsed, setOpportunitiesCollapsed] = useState(true);
  const [experienceGapsCollapsed, setExperienceGapsCollapsed] = useState(true);
  const [actionPlaybookCollapsed, setActionPlaybookCollapsed] = useState(true);
  const [kpiHypothesesCollapsed, setKpiHypothesesCollapsed] = useState(true);
  const [openQuestionsCollapsed, setOpenQuestionsCollapsed] = useState(true);
  const [analyticsCollapsed, setAnalyticsCollapsed] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [interviewModalOpen, setInterviewModalOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const comprehensiveRef = useRef(null);
  const hasResults = !!reportDoc && Object.keys(reportDoc || {}).length > 0;

  const sortedBehaviours = useMemo(() => {
    if (!Array.isArray(reportDoc?.report?.behaviours)) return [];

    return reportDoc.report.behaviours
      .slice()
      .sort((a, b) => {
        const fa = typeof a.frequency === 'number' ? a.frequency : 0;
        const fb = typeof b.frequency === 'number' ? b.frequency : 0;
        if (fb !== fa) return fb - fa;
        const ca = typeof a.averageConfidence === 'number' ? a.averageConfidence : 0;
        const cb = typeof b.averageConfidence === 'number' ? b.averageConfidence : 0;
        return cb - ca;
      })
      .slice(0, 5);
  }, [reportDoc]);

  const fetchReport = async () => {
    if (!canvassId) return;
    setLoading(true);
    setError('');
    try {
      const res = await getCanvassReport(canvassId, { returnFull: true });
      setReportDoc(res?.report || null);
      // Also set executive summary if it exists in the report object
      setExecutiveSummary(
        res?.report?.adminExecutiveSummary ||
        res?.report?.comprehensiveReport?.adminExecutiveSummary ||
        res?.report?.report?.adminExecutiveSummary
      );
    } catch (e) {
      setError(e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [canvassId]);

  const downloadJson = () => {
    downloadReportJson(canvassId, reportDoc, canvass?.canvassName || 'Canvass');
  };

  const exportComprehensiveReport = () => {
    exportComprehensiveReportHtml(reportDoc, canvass?.canvassName || 'Canvass');
  };

  const handleAdminSummaryUpdated = async () => {
    // Refresh the report data to get the updated admin summary
    await fetchReport();
  };

  const generateExecutiveSummary = async () => {
    if (!canvassId) return;
    setExecutiveSummaryLoading(true);
    setError('');
    try {
      const res = await generateExecutiveSummaryApi(canvassId);
      // Set the executive summary in its own state
      setExecutiveSummary(res?.executiveSummary || res?.summary || null);
    } catch (e) {
      setError(e?.message || 'Failed to generate executive summary');
    } finally {
      setExecutiveSummaryLoading(false);
    }
  };

  const exportCombinedJson = async () => {
    try {
      if (!canvassId) return;
      // Build canvass JSON similar to export in CanvassesTable.jsx
      const startTime = reportDoc?.report?.deployment?.startTime
        || canvass?.timestamp
        || new Date().toISOString();
      const endTime = reportDoc?.report?.deployment?.tearDownTime
        || canvass?.timestamp
        || new Date().toISOString();

      const canvassJson = {
        ...canvass,
        conversationId: String(canvassId),
        id: String(canvassId),
        startTime,
        endTime,
        userMessageCount: 0,
        messageCount: 0,
        messages: [],
        includeMessages: false,
      };

      // Fetch full interviews payload
      const interviewsPayload = await fetchInterviewsByCanvassId({
        canvassId,
        page: 1,
        pageSize: 50,
        returnFull: true,
      });

      const combined = {
        canvass: canvassJson,
        interviews: interviewsPayload,
        report: reportDoc || null,
        generatedAt: new Date().toISOString(),
      };

      const dataStr = JSON.stringify(combined, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const nameBase = canvass?.canvassName || 'Canvass';
      const safeName = String(nameBase).replace(/[^a-z0-9_-]+/gi, '_');
      const filename = `${safeName}_Export_${canvassId || 'unknown'}.json`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      logger.error('Failed to export canvass JSON', e);
      if (typeof window !== 'undefined') {
        window.alert(e?.message || 'Failed to export canvass data');
      }
    }
  };

  const formatDuration = (minutes) => {
    if (typeof minutes !== 'number' || !isFinite(minutes)) return '—';
    if (minutes >= 1440) return `${(minutes / 1440).toFixed(1)} days`;
    if (minutes >= 60) return `${(minutes / 60).toFixed(1)} hours`;
    return `${minutes} min`;
  };

  const handleSnippetClick = (sessionId) => {
    if (sessionId) {
      setSelectedSessionId(sessionId);
      setInterviewModalOpen(true);
    }
  };

  const closeInterviewModal = () => {
    setInterviewModalOpen(false);
    setSelectedSessionId(null);
  };

  const fetchAnalyticsData = async () => {
    if (!canvassId) return;
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const data = await analyticsService.getCanvassAnalytics(canvassId);
      // Handle different response structures
      const analyticsData = data?.data?.canvassAnalytics || data?.canvassAnalytics || data;
      setAnalyticsData(analyticsData);
    } catch (e) {
      setAnalyticsError(e?.message || 'Failed to fetch analytics data');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (canvassId && !analyticsCollapsed) {
      fetchAnalyticsData();
    }
  }, [canvassId, analyticsCollapsed]);

  return (
    <>
        {!loading && !error && !hasResults && (
            <div className={reportStyles.textSecondary + ' ' + reportStyles.none}>Results not yet available for this Canvass, check back later.</div>
        )}

        {!loading && !error && hasResults && (
            <>
                <section className={styles.section + ' ' + styles.sectionFirst}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionTitle}>Results</div>
                </div>

                {loading && (
                    <div style={{ color: 'var(--text-secondary, #6c757d)' }}>Loading…</div>
                )}

                {!loading && error && (
                    <div style={{ color: 'var(--brand-as-red-700, #b91c1c)' }}>{error}</div>
                )}

                {!loading && !error && !reportDoc && (
                    <div className={reportStyles.textSecondary}>No results are available yet.</div>
                )}

                {!loading && !error && reportDoc && (
                    <>
                    <div className={styles.section}>
                        <div className={`${reportStyles.list} ${reportStyles.textLeft}`}>
                        <div className={reportStyles.listRow}>
                            <span className={reportStyles.listKey}>Objective:</span>
                            <span className={reportStyles.listValue}>{canvass?.objective || '-'}</span>
                        </div>
                        <div className={reportStyles.listRow}>
                            <span className={reportStyles.listKey}>Completed Voices:</span>
                            <span className={reportStyles.listValue}>{reportDoc?.report?.performance?.completedVoices ?? '—'}</span>
                        </div>
                        <div className={reportStyles.listRow}>
                            <span className={reportStyles.listKey}>Incomplete Voices:</span>
                            <span className={reportStyles.listValue}>{reportDoc?.report?.performance?.abandonedVoices ?? '—'}</span>
                        </div>
                        <div className={reportStyles.listRow}>
                            <span className={reportStyles.listKey}>Start Time:</span>
                            <span className={reportStyles.listValue}>{reportDoc?.report?.deployment?.startTime ? new Date(reportDoc.report.deployment.startTime).toLocaleString() : '—'}</span>
                        </div>
                        {reportDoc?.report?.deployment?.tearDownTime && canvass?.status === 'complete' && (
                            <div className={reportStyles.listRow}>
                            <span className={reportStyles.listKey}>Tear Down Time:</span>
                            <span className={reportStyles.listValue}>{new Date(reportDoc.report.deployment.tearDownTime).toLocaleString()}</span>
                            </div>
                        )}
                        {typeof reportDoc?.report?.deployment?.durationMinutes === 'number' && (
                            <div className={reportStyles.listRow}>
                            <span className={reportStyles.listKey}>Duration:</span>
                            <span className={reportStyles.listValue}>{formatDuration(reportDoc.report.deployment.durationMinutes)}</span>
                            </div>
                        )}
                        </div>
                    </div>
                    </>
                )}
                </section>

                {/* Analytics Section */}
                <div className={styles.modalSeparator} />
                <section>
                    <div className={styles.sectionHeader}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className={styles.sectionTitle}>Analytics</div>
                            <button
                                type="button"
                                aria-label={analyticsCollapsed ? 'Expand' : 'Collapse'}
                                onClick={() => setAnalyticsCollapsed(v => !v)}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                            >
                                {analyticsCollapsed ? (
                                  <Icon name="arrows/arrow_right" width={28} height={28} color="var(--system-sky-600)"/>
                                ) : (
                                  <Icon name="arrows/arrow_drop_down" width={28} height={28} color="var(--system-sky-600)"/>
                                )}
                            </button>
                        </div>
                       { !analyticsCollapsed ? ( <div className={styles.sectionSubTitle + ' ' + reportStyles.textSecondary}>
                            Detailed analytics and metrics for this canvass.
                        </div> ) : (<div></div>)}
                    </div>
                    {!analyticsCollapsed && (
                        <>
                            {analyticsLoading && (
                                <div style={{ color: 'var(--text-secondary, #6c757d)', padding: '16px' }}>Loading analytics data…</div>
                            )}
                            {analyticsError && (
                                <div style={{ color: 'var(--system-red-600, #dc3545)', padding: '16px' }}>
                                    Error: {analyticsError}
                                </div>
                            )}
                            {analyticsData && !analyticsLoading && !analyticsError && (
                                <div className={`${reportStyles.list} ${reportStyles.textLeft}`}>
                                    <div className={reportStyles.listRow}>
                                        <span className={reportStyles.listKey}>Total Interviews:</span>
                                        <span className={reportStyles.listValue}>{analyticsData.interviewCount || 0}</span>
                                    </div>
                                    <div className={reportStyles.listRow}>
                                        <span className={reportStyles.listKey}>Completed Interviews:</span>
                                        <span className={reportStyles.listValue}>{analyticsData.completedInterviews || 0}</span>
                                    </div>
                                    <div className={reportStyles.listRow}>
                                        <span className={reportStyles.listKey}>Total Questions Asked:</span>
                                        <span className={reportStyles.listValue}>{analyticsData.totalQuestions || 0}</span>
                                    </div>
                                    <div className={reportStyles.listRow}>
                                        <span className={reportStyles.listKey}>Engagement Score:</span>
                                        <span className={reportStyles.listValue}>{analyticsData.engagementScore ? `${(analyticsData.engagementScore * 100).toFixed(1)}%` : '—'}</span>
                                    </div>
                                    {analyticsData.topEmotions && analyticsData.topEmotions.length > 0 && (
                                        <div className={reportStyles.listRow}>
                                            <span className={reportStyles.listKey}>Top Emotions:</span>
                                            <span className={reportStyles.listValue}>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                    {analyticsData.topEmotions.slice(0, 3).map((emotion, index) => (
                                                        <span 
                                                            key={index}
                                                            style={{
                                                                display: 'inline-block',
                                                                padding: '4px 10px',
                                                                backgroundColor: 'var(--brand-as-blue-100, #e3f2fd)',
                                                                color: 'var(--brand-as-blue-800, #1565c0)',
                                                                borderRadius: '16px',
                                                                fontSize: '0.875rem',
                                                                fontWeight: '500',
                                                                border: '1px solid var(--brand-as-blue-200, #bbdefb)'
                                                            }}
                                                        >
                                                            {emotion.emotion} ({(emotion.averageStrength * 100).toFixed(1)}%)
                                                        </span>
                                                    ))}
                                                </div>
                                            </span>
                                        </div>
                                    )}
                                    {analyticsData.completionRate !== undefined && (
                                        <div className={reportStyles.listRow}>
                                            <span className={reportStyles.listKey}>Completion Rate:</span>
                                            <span className={reportStyles.listValue}>{(analyticsData.completionRate).toFixed(1)}%</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {!analyticsData && !analyticsLoading && !analyticsError && (
                                <div style={{ color: 'var(--text-secondary, #6c757d)', padding: '16px' }}>
                                    No analytics data available for this canvass.
                                </div>
                            )}
                        </>
                    )}
                </section>

                {/* <div className={styles.modalSeparator} />
                <section>
                    <div className={styles.sectionHeader}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className={styles.sectionTitle}>Sentiment</div>
                            <button
                                type="button"
                                aria-label={sentimentCollapsed ? 'Expand' : 'Collapse'}
                                onClick={() => setSentimentCollapsed(v => !v)}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                            >
                                {sentimentCollapsed ? (
                                  <Icon name="arrows/arrow_right" width={28} height={28} color="var(--system-sky-600)"/>
                                ) : (
                                  <Icon name="arrows/arrow_drop_down" width={28} height={28} color="var(--system-sky-600)"/>
                                )}
                            </button>
                        </div>
                       { !sentimentCollapsed ? ( <div className={styles.sectionSubTitle + ' ' + reportStyles.textSecondary}>
                            Values plotted are average strength (0–1) per emotion.
                        </div> ) : (<div></div>)}
                    </div>
                    {!sentimentCollapsed && (
                    <div className={reportStyles.sentimentGrid}>

                        {Array.isArray(reportDoc?.report?.sentiment?.topEmotions) && reportDoc.report.sentiment.topEmotions.length > 0 ? (
                        <div className={reportStyles.sentimentChartRow}>
                            <RadarChart
                            size={typeof window !== 'undefined' && window.innerWidth <= 767 ? 260 : 360}
                            data={reportDoc.report.sentiment.topEmotions.map(e => ({ label: e.emotion, value: e.averageStrength }))}
                            onHoverChange={setHoveredEmotion}
                            />
                            <div className={`${reportStyles.smallNote} ${reportStyles.sentimentHoverNote}`}>
                            {hoveredEmotion ? (
                                <>
                                <span style={{ fontWeight: 600 }}>{hoveredEmotion.label}</span>: {typeof hoveredEmotion.value === 'number' ? hoveredEmotion.value.toFixed(2) : '—'}
                                </>
                            ) : (
                                'Hover over a label to see its value'
                            )}
                            </div>
                        </div>
                        ) : (
                        <div className={reportStyles.textSecondary}>No emotion data available.</div>
                        )}
                        <div className={`${reportStyles.textSecondary} ${reportStyles.mb8} ${reportStyles.sentimentAvgRow}`}>
                            Avg Sentiment: {typeof reportDoc?.report?.sentiment?.averageSentimentScore === 'number' ? reportDoc.report.sentiment.averageSentimentScore.toFixed(2) : '—'}
                        </div>
                    </div>
                    )}
                </section> */}
                <div className={styles.modalSeparator} />
                <section>
                  <div className={styles.sectionHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={styles.sectionTitle}>Top Snippets</div>
                      <button
                        type="button"
                        aria-label={snippetsCollapsed ? 'Expand' : 'Collapse'}
                        onClick={() => setSnippetsCollapsed(v => !v)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                      >
                        {snippetsCollapsed ? (
                          <Icon name="arrows/arrow_right" width={28} height={28} color="var(--system-sky-600)" />
                        ) : (
                          <Icon name="arrows/arrow_drop_down" width={28} height={28} color="var(--system-sky-600)" />
                        )}
                      </button>
                    </div>
                  </div>
                  {!snippetsCollapsed && (
                    <div className={reportStyles.pillsRow}>
                      {Array.isArray(reportDoc?.report?.topSnippets) && reportDoc.report.topSnippets.length > 0 ? (
                        reportDoc.report.topSnippets.map((s, idx) => (
                          <div 
                            key={idx} 
                            className={`${reportStyles.pill} ${s.sessionId ? reportStyles.clickablePill : ''}`}
                            onClick={() => s.sessionId && handleSnippetClick(s.sessionId)}
                            title={s.sessionId ? `Click to view full interview (${s.sessionId})` : undefined}
                          >
                            <span className={reportStyles.pillEmotion}>{s.emotion ?? '—'}</span>
                            <span className={reportStyles.pillStrength}>{typeof s.strength === 'number' ? s.strength.toFixed(2) : '—'}</span>
                            <span className={reportStyles.pillText}>{s.text ?? ''}</span>
                            {s.sessionId && (
                              <span className={reportStyles.pillIndicator}>
                                <Icon name="arrows/arrow_right" width={12} height={12} />
                              </span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className={reportStyles.textSecondary}>No snippets available.</div>
                      )}
                    </div>
                  )}
                </section>
                {canvass?.status !== 'complete' && (
                <>
                  <div className={styles.modalSeparator} />
                  <section>
                  <div className={styles.sectionHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={styles.sectionTitle}>Executive Summary</div>
                      <button
                        type="button"
                        aria-label={executiveSummaryCollapsed ? 'Expand' : 'Collapse'}
                        onClick={() => setExecutiveSummaryCollapsed(v => !v)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                      >
                        {executiveSummaryCollapsed ? (
                          <Icon name="arrows/arrow_right" width={28} height={28} color="var(--system-sky-600)" />
                        ) : (
                          <Icon name="arrows/arrow_drop_down" width={28} height={28} color="var(--system-sky-600)" />
                        )}
                      </button>
                    </div>
                  </div>
                  {!executiveSummaryCollapsed && (
                    <div className={reportStyles.textLeft}>
                      {executiveSummaryLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '1em 0' }}>
                          <Icon name="loading/spinner" width={20} height={20} color="var(--system-sky-600)" />
                          <span className={reportStyles.textSecondary}>Generating executive summary...</span>
                        </div>
                      ) : executiveSummary ? (
                        <div className={reportStyles.textLeft}>
                          {executiveSummary.split('\n\n').map((paragraph, index) => (
                            <p 
                              key={index} 
                              style={{ marginBottom: '1em', lineHeight: '1.5' }}
                              dangerouslySetInnerHTML={{
                                __html: paragraph.split('\n').map((line, lineIndex) => {
                                  const processedLine = parseMarkdownFormatting(line);
                                  return lineIndex > 0 ? `<br>${processedLine}` : processedLine;
                                }).join('')
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className={reportStyles.textSecondary}>No executive summary available. Click "Generate Executive Summary" to create one.</div>
                      )}
                    </div>
                  )}
                </section>
                </>
              )}
                {/* <div className={styles.modalSeparator} />
                <section>
                    <div className={styles.sectionHeader}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className={styles.sectionTitle}>Behaviours</div>
                          <button
                            type="button"
                            aria-label={behavioursCollapsed ? 'Expand' : 'Collapse'}
                            onClick={() => setBehavioursCollapsed(v => !v)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                          >
                            {behavioursCollapsed ? (
                              <Icon name="arrows/arrow_right" width={28} height={28} color="var(--system-sky-600)" />
                            ) : (
                              <Icon name="arrows/arrow_drop_down" width={28} height={28} color="var(--system-sky-600)" />
                            )}
                          </button>
                        </div>
                    </div>
                    {!behavioursCollapsed && (
                      <>
                        {sortedBehaviours.length > 0 ? (
                          <>
                            <div className={reportStyles.behaviourTableDesktop}>
                              <SimpleTable
                                headers={[
                                  'Behaviour',
                                  'Frequency',
                                  'Confidence',
                                  'Occurrences',
                                ]}
                              >
                                {sortedBehaviours.map((b, idx) => (
                                  <tr key={idx}>
                                    <td>{typeof b.behaviour === 'string' ? b.behaviour.replace(/_/g, ' ') : '—'}</td>
                                    <td>{typeof b.frequency === 'number' ? `${(b.frequency * 100).toFixed(1)}%` : '—'}</td>
                                    <td>{typeof b.averageConfidence === 'number' ? b.averageConfidence.toFixed(2) : '—'}</td>
                                    <td>{typeof b.totalOccurrences === 'number' ? b.totalOccurrences : '—'}</td>
                                  </tr>
                                ))}
                              </SimpleTable>
                            </div>

                            <div className={reportStyles.behaviourCardsMobile}>
                              {sortedBehaviours.map((b, idx) => (
                                <div key={idx} className={reportStyles.behaviourCard}>
                                  <div className={reportStyles.behaviourCardTitle}>
                                    {typeof b.behaviour === 'string' ? b.behaviour.replace(/_/g, ' ') : '—'}
                                  </div>
                                  <div className={reportStyles.behaviourCardGrid}>
                                    <div className={reportStyles.behaviourCardItem}>
                                      <span className={reportStyles.behaviourCardLabel}>Frequency</span>
                                      <span className={reportStyles.behaviourCardValue}>
                                        {typeof b.frequency === 'number' ? `${(b.frequency * 100).toFixed(1)}%` : '—'}
                                      </span>
                                    </div>
                                    <div className={reportStyles.behaviourCardItem}>
                                      <span className={reportStyles.behaviourCardLabel}>Confidence</span>
                                      <span className={reportStyles.behaviourCardValue}>
                                        {typeof b.averageConfidence === 'number' ? b.averageConfidence.toFixed(2) : '—'}
                                      </span>
                                    </div>
                                    <div className={reportStyles.behaviourCardItem}>
                                      <span className={reportStyles.behaviourCardLabel}>Occurrences</span>
                                      <span className={reportStyles.behaviourCardValue}>
                                        {typeof b.totalOccurrences === 'number' ? b.totalOccurrences : '—'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className={reportStyles.textSecondary}>No behaviours available.</div>
                        )}
                      </>
                    )}
                </section> */}
                {/* <div className={styles.modalSeparator} />
                <section>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionTitle}>Raw Report</div>
                    </div>
                    <div className={reportStyles.jsonContainer}>
                        <pre className={reportStyles.preCode}>{JSON.stringify(reportDoc, null, 4)}</pre>
                    </div>
                </section> */}
                {reportDoc?.report?.businessIntelligence && typeof reportDoc.report.businessIntelligence === 'object' && (
                  <>
                    <div className={styles.modalSeparator} />
                    <div className={styles.section}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd} ${cbStyles.sectionHeaderPadTopMd}`}>
                          Business Intelligence
                        </div>
                        <button
                          type="button"
                          aria-label={businessIntelligenceCollapsed ? 'Expand' : 'Collapse'}
                          onClick={() => setBusinessIntelligenceCollapsed(v => !v)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                        >
                          {businessIntelligenceCollapsed ? (
                            <Icon
                              name="arrows/arrow_right"
                              width={28}
                              height={28}
                              color="var(--system-sky-600)"
                            />
                          ) : (
                            <Icon
                              name="arrows/arrow_drop_down"
                              width={28}
                              height={28}
                              color="var(--system-sky-600)"
                            />
                          )}
                        </button>
                      </div>
                      <div className={cbStyles.sectionHeaderPadSm}></div>

                      {!businessIntelligenceCollapsed && (
                        <>
                          {Array.isArray(reportDoc.report.businessIntelligence.coreEmotionalDrivers) && reportDoc.report.businessIntelligence.coreEmotionalDrivers.length > 0 && (
                            <div className={reportStyles.mb8}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div className={`${styles.sectionSubTitle} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPad} ${cbStyles.sectionSubHeader}`}>
                                  Core Emotional Drivers
                                </div>
                                <button
                                  type="button"
                                  aria-label={coreEmotionalDriversCollapsed ? 'Expand' : 'Collapse'}
                                  onClick={() => setCoreEmotionalDriversCollapsed(v => !v)}
                                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                                >
                                  {coreEmotionalDriversCollapsed ? (
                                    <Icon
                                      name="arrows/arrow_right"
                                      width={28}
                                      height={28}
                                      color="var(--system-sky-600)"
                                    />
                                  ) : (
                                    <Icon
                                      name="arrows/arrow_drop_down"
                                      width={28}
                                      height={28}
                                      color="var(--system-sky-600)"
                                    />
                                  )}
                                </button>
                              </div>
                              {!coreEmotionalDriversCollapsed && (
                                <div className={cbStyles.emotionalBeatsGrid}>
                                  {reportDoc.report.businessIntelligence.coreEmotionalDrivers.map((driver, i) => (
                                    <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                                      <div className={cbStyles.beatStage}>{driver?.theme || '—'}</div>
                                      {driver?.pattern && (
                                        <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                          {formatValue(driver.pattern)}
                                        </div>
                                      )}
                                      {Array.isArray(driver?.needs) && driver.needs.length > 0 && (
                                        <div className={reportStyles.mb8}>
                                          <div className={reportStyles.textSecondary}>Needs</div>
                                          {renderPills(driver.needs, `driver-needs-${i}`)}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {Array.isArray(reportDoc.report.businessIntelligence.operationalRisks) && reportDoc.report.businessIntelligence.operationalRisks.length > 0 && (
                            <div className={reportStyles.mb8}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div className={`${styles.sectionSubTitle} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPad} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadTopMd}`}>Operational Risks</div>
                                <button
                                  type="button"
                                  aria-label={operationalRisksCollapsed ? 'Expand' : 'Collapse'}
                                  onClick={() => setOperationalRisksCollapsed(v => !v)}
                                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                                >
                                  {operationalRisksCollapsed ? (
                                    <Icon
                                      name="arrows/arrow_right"
                                      width={28}
                                      height={28}
                                      color="var(--system-sky-600)"
                                    />
                                  ) : (
                                    <Icon
                                      name="arrows/arrow_drop_down"
                                      width={28}
                                      height={28}
                                      color="var(--system-sky-600)"
                                    />
                                  )}
                                </button>
                              </div>
                              {!operationalRisksCollapsed && (
                                <div className={cbStyles.emotionalBeatsGrid}>
                                  {reportDoc.report.businessIntelligence.operationalRisks.map((risk, i) => (
                                    <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                                      <div className={cbStyles.beatStage}>{risk?.area || '—'}</div>
                                      {risk?.risk && (
                                        <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                          {formatValue(risk.risk)}
                                        </div>
                                      )}
                                      {risk?.emotionalImpact && (
                                        <div className={reportStyles.mb8}>
                                          <div className={reportStyles.textSecondary}>Emotional Impact</div>
                                          <div className={reportStyles.listValue}>{formatValue(risk.emotionalImpact)}</div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {Array.isArray(reportDoc.report.businessIntelligence.opportunities) && reportDoc.report.businessIntelligence.opportunities.length > 0 && (
                            <div className={reportStyles.mb8}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div className={`${styles.sectionSubTitle} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPad} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadTopMd}`}>Oppourtunities</div>
                                <button
                                  type="button"
                                  aria-label={opportunitiesCollapsed ? 'Expand' : 'Collapse'}
                                  onClick={() => setOpportunitiesCollapsed(v => !v)}
                                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                                >
                                  {opportunitiesCollapsed ? (
                                    <Icon
                                      name="arrows/arrow_right"
                                      width={28}
                                      height={28}
                                      color="var(--system-sky-600)"
                                    />
                                  ) : (
                                    <Icon
                                      name="arrows/arrow_drop_down"
                                      width={28}
                                      height={28}
                                      color="var(--system-sky-600)"
                                    />
                                  )}
                                </button>
                              </div>
                              {!opportunitiesCollapsed && (
                                <div className={cbStyles.emotionalBeatsGrid}>
                                  {reportDoc.report.businessIntelligence.opportunities.map((opp, i) => (
                                    <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                                      <div className={cbStyles.beatStage}>{opp?.area || '—'}</div>
                                      {opp?.play && (
                                        <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                          {formatValue(opp.play)}
                                        </div>
                                      )}
                                      {opp?.benefit && (
                                        <div className={reportStyles.mb8}>
                                          <div className={reportStyles.textSecondary}>Benefit</div>
                                          <div className={reportStyles.listValue}>{formatValue(opp.benefit)}</div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
                {reportDoc?.comprehensiveReport && (
                  <>
                    <div className={styles.modalSeparator} />
                    <section ref={comprehensiveRef}>
                      <ComprehensiveBusinessReport 
                        data={reportDoc.comprehensiveReport ? {...reportDoc.comprehensiveReport, ...(executiveSummary ? { adminExecutiveSummary: executiveSummary } : {})} : {adminExecutiveSummary: executiveSummary}}
                        performance={reportDoc.report.performance}
                        canvassId={canvassId} 
                        onAdminSummaryUpdated={handleAdminSummaryUpdated}
                      />
                    </section>
                  </>
                )}
                <section>
                    <div className={reportStyles.actionsRow}>
                        <Button
                        variant="outline-neutral"
                        text="Refresh"
                        onClick={fetchReport}
                        disabled={loading}
                        >
                        Refresh
                        </Button>
                        {canvass?.status !== 'complete' && (
                          <Button
                          variant="primary"
                          text="Generate Executive Summary"
                          onClick={generateExecutiveSummary}
                          disabled={executiveSummaryLoading || !canvassId}
                          >
                          Generate Summary
                          </Button>
                        )}
                        {/* {canvass?.status === 'complete' && ( */}
                          <>
                            {/* <Button
                              variant="primary"
                              text="Export Combined"
                              onClick={exportCombinedJson}
                              disabled={!canvassId}
                            >
                              Export Combined
                            </Button> */}
                            {reportDoc?.comprehensiveReport && (
                              <Button
                                variant="primary"
                                text="Export Comprehensive"
                                onClick={exportComprehensiveReport}
                                disabled={!canvassId}
                              >
                                Export Comprehensive
                              </Button>
                            )}
                          </>
                        {/* )} */}
                        {/* <Button
                        variant="primary"
                        iconLeft={{ name: 'arrows/arrow_downward' }}
                        text="Download JSON"
                        onClick={downloadJson}
                        disabled={!reportDoc}
                        >
                        Download JSON
                        </Button> */}
                    </div>
                </section>
            </>
        )}
        
        <InterviewModal
          isOpen={interviewModalOpen}
          onClose={closeInterviewModal}
          sessionId={selectedSessionId}
          canvassId={canvassId}
        />
    </>
  );
};

export default ReportSection;
